/** SUNTRONIX ONE — SERVICE HUB (unified external service experience).
 *  Cases (support/repair/RMA/warranty/replacement/recovery), Warranty &
 *  AppleCare overview, and Raise-a-request — all over existing records. */
import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  Wrench, ShieldCheck, Loader2, Plus, X, LifeBuoy, ClipboardList, PackageCheck,
} from "lucide-react";

const fmtDate = (d) => (d ? new Date(d).toLocaleDateString() : "—");
const inputCls = "w-full rounded-md border border-[color:var(--st-border)] bg-[color:var(--st-surface)] px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-[color:var(--st-accent)]";

export default function ServiceHub() {
  const [data, setData] = useState(null);
  const [tab, setTab] = useState("cases");
  const [open, setOpen] = useState(false);
  const [err, setErr] = useState(null);

  const load = () => api.get("/service-hub/home").then(({ data }) => setData(data)).catch((e) => setErr(e?.response?.data?.detail || "Could not load Service Hub"));
  useEffect(() => { load(); }, []);

  if (err) return <div className="p-6 text-sm text-[color:var(--st-text-muted)]" data-testid="service-hub-error">{err}</div>;
  if (!data) return <div className="p-6 flex items-center gap-2 text-sm text-[color:var(--st-text-subtle)]"><Loader2 className="h-4 w-4 animate-spin" /> Loading Service Hub…</div>;

  const c = data.counts;
  return (
    <div className="max-w-3xl mx-auto space-y-6" data-testid="service-hub">
      <div className="flex items-start gap-3">
        <div className="grid h-11 w-11 place-items-center rounded-xl" style={{ background: "color-mix(in srgb, var(--st-accent) 12%, transparent)", color: "var(--st-accent)" }}>
          <Wrench className="h-6 w-6" strokeWidth={1.7} />
        </div>
        <div>
          <h1 className="text-2xl font-semibold tracking-tight text-[color:var(--st-text)]">Service Hub</h1>
          <p className="text-sm text-[color:var(--st-text-muted)]">Support, repairs, RMA, warranty & AppleCare — all in one place.</p>
        </div>
        <Button className="ml-auto" data-testid="svc-raise-btn" onClick={() => setOpen(true)}><Plus className="h-4 w-4 mr-1.5" /> Raise service request</Button>
      </div>

      <div className="grid grid-cols-3 gap-3">
        <Stat icon={LifeBuoy} label="Open cases" v={c.open_cases} />
        <Stat icon={ClipboardList} label="Total cases" v={c.total_cases} />
        <Stat icon={ShieldCheck} label="AppleCare devices" v={c.applecare} />
      </div>

      <div className="flex gap-1.5 border-b border-[color:var(--st-border)] pb-2">
        {[["cases", "Cases"], ["warranty", "Warranty & AppleCare"]].map(([k, l]) => (
          <button key={k} data-testid={`svc-tab-${k}`} onClick={() => setTab(k)}
            className={"px-3 py-1.5 rounded-md text-[13px] font-medium " + (tab === k ? "bg-[color:var(--st-text)] text-white" : "text-[color:var(--st-text-muted)] hover:bg-[color:var(--st-surface-alt)]")}>{l}</button>
        ))}
      </div>

      {tab === "cases" && (
        data.cases.length === 0 ? <Empty label="No service cases yet." />
          : <div className="space-y-2" data-testid="svc-cases">{data.cases.map((t) => (
              <div key={t.id} className="rounded-xl border border-[color:var(--st-border)] bg-[color:var(--st-surface)] p-4" data-testid={`svc-case-${t.ticket_number}`}>
                <div className="flex items-center gap-2">
                  <span className="font-mono text-xs text-[color:var(--st-text-subtle)]">{t.ticket_number}</span>
                  <span className="text-[11px] rounded-full bg-[color:var(--st-surface-alt)] px-2 py-0.5 text-[color:var(--st-text-muted)]">{(t.status || "").replace(/_/g, " ")}</span>
                  <span className="text-[11px] text-[color:var(--st-text-subtle)]">{(t.type || "").replace(/_/g, " ")}</span>
                </div>
                <div className="mt-1 font-medium text-[color:var(--st-text)]">{t.subject}</div>
                <div className="text-xs text-[color:var(--st-text-subtle)]">{[t.product, t.serial, fmtDate(t.created_at)].filter(Boolean).join(" · ")}</div>
              </div>
            ))}</div>
      )}

      {tab === "warranty" && (
        data.warranty.length === 0 ? <Empty label="No devices with warranty data." />
          : <div className="space-y-2" data-testid="svc-warranty">{data.warranty.map((w) => (
              <div key={w.id} className="rounded-xl border border-[color:var(--st-border)] bg-[color:var(--st-surface)] p-4 flex items-center gap-3" data-testid={`svc-warranty-${w.id}`}>
                <div className="grid h-10 w-10 place-items-center rounded-lg bg-[color:var(--st-surface-alt)]"><PackageCheck className="h-5 w-5 text-[color:var(--st-text-muted)]" /></div>
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-[color:var(--st-text)] truncate">{w.product}</div>
                  <div className="text-xs text-[color:var(--st-text-subtle)]">{w.serial}</div>
                </div>
                <div className="text-right text-[11px]">
                  <div><span className={w.warranty_covered ? "text-emerald-600" : "text-slate-500"}>Warranty {fmtDate(w.warranty_end)}</span></div>
                  {w.applecare_end && <div className="text-[color:var(--st-text-subtle)]">AppleCare {fmtDate(w.applecare_end)}</div>}
                </div>
              </div>
            ))}</div>
      )}

      {open && <RaiseDrawer categories={data.categories} onClose={() => setOpen(false)} onDone={() => { setOpen(false); load(); }} />}
    </div>
  );
}

function Stat({ icon: Icon, label, v }) {
  return (
    <div className="rounded-xl border border-[color:var(--st-border)] bg-[color:var(--st-surface)] p-4">
      <div className="grid h-9 w-9 place-items-center rounded-lg" style={{ background: "color-mix(in srgb, var(--st-accent) 12%, transparent)", color: "var(--st-accent)" }}><Icon className="h-5 w-5" strokeWidth={1.7} /></div>
      <div className="mt-2 text-2xl font-semibold text-[color:var(--st-text)]">{v}</div>
      <div className="text-[12px] text-[color:var(--st-text-muted)]">{label}</div>
    </div>
  );
}

function RaiseDrawer({ categories, onClose, onDone }) {
  const [f, setF] = useState({ category: categories[0]?.key, subject: "", description: "", serial: "" });
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const submit = async () => {
    if (f.subject.trim().length < 3 || f.description.trim().length < 3) { toast.error("Please add a subject and description"); return; }
    const cat = categories.find((c) => c.key === f.category);
    setBusy(true);
    try {
      await api.post("/service-desk/portal/requests", { ticket_type: cat?.type || "OTHER", category: cat?.label, subject: f.subject, description: f.description, serial: f.serial });
      toast.success("Service request submitted");
      onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not submit"); }
    finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-50 flex justify-end" data-testid="svc-raise-drawer">
      <div className="absolute inset-0 bg-black/30" onClick={onClose} />
      <div className="relative w-full max-w-md bg-[color:var(--st-surface)] h-full overflow-y-auto p-5 space-y-4 shadow-2xl">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold">Raise a service request</h2>
          <button onClick={onClose} className="h-8 w-8 grid place-items-center rounded-md hover:bg-[color:var(--st-surface-alt)]"><X className="h-4 w-4" /></button>
        </div>
        <label className="block"><span className="text-[12px] font-medium text-[color:var(--st-text-muted)]">Category</span>
          <select className={"mt-1 " + inputCls} data-testid="svc-req-category" value={f.category} onChange={(e) => set("category", e.target.value)}>
            {categories.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
          </select></label>
        <label className="block"><span className="text-[12px] font-medium text-[color:var(--st-text-muted)]">Subject *</span>
          <input className={"mt-1 " + inputCls} data-testid="svc-req-subject" value={f.subject} onChange={(e) => set("subject", e.target.value)} /></label>
        <label className="block"><span className="text-[12px] font-medium text-[color:var(--st-text-muted)]">Details *</span>
          <textarea className={"mt-1 " + inputCls} rows={4} data-testid="svc-req-desc" value={f.description} onChange={(e) => set("description", e.target.value)} /></label>
        <label className="block"><span className="text-[12px] font-medium text-[color:var(--st-text-muted)]">Device serial (optional)</span>
          <input className={"mt-1 " + inputCls} data-testid="svc-req-serial" value={f.serial} onChange={(e) => set("serial", e.target.value)} /></label>
        <Button className="w-full" onClick={submit} disabled={busy} data-testid="svc-req-submit">{busy ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : null} Submit request</Button>
      </div>
    </div>
  );
}

function Empty({ label }) {
  return <div className="rounded-xl border border-dashed border-[color:var(--st-border)] p-8 text-center text-sm text-[color:var(--st-text-muted)]" data-testid="svc-empty">{label}</div>;
}
